import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, DollarSign, CreditCard, Tag, Edit2, Calendar, Clock, ListOrdered } from 'lucide-react';
import { useBookingActions, useSlotAvailability, useSlotConfiguration } from '../hooks/booking/useBookingContextSlices';
import { waitlistAPI } from '../services/api';
import { format, addDays } from 'date-fns';
import Notification from '../components/Notification';
import DashboardLayout from '../components/DashboardLayout';
import { InfoBanner } from '../components/ui';

// GA-005: friendly labels for the pre-submit error summary.
const FIELD_LABELS = {
  service: 'Service',
  duration: 'Duration',
  date: 'Date',
  slot: 'Time slot',
  name: 'Name',
  contactPhone: 'Contact number',
};

// GA-005: on validation failure, bring the first invalid field into view and
// focus it. Fields are tagged with `data-field="<errorKey>"` and walked in DOM
// order so the top-most invalid field wins.
const focusFirstInvalidField = (errs) => {
  if (typeof document === 'undefined' || !errs) return;
  const keys = Object.keys(errs).filter(
    (k) => errs[k] && k !== 'submit' && errs[k] !== 'SLOT_FULL'
  );
  if (!keys.length) return;
  const nodes = Array.from(document.querySelectorAll('[data-field]'));
  const target =
    nodes.find((n) => keys.includes(n.getAttribute('data-field'))) ||
    document.querySelector(`[data-field="${keys[0]}"]`);
  if (!target) return;
  try {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch (_) {
    target.scrollIntoView();
  }
  target.focus?.();
};

const BookingNew = () => {
  const { groundId, serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addBooking } = useBookingActions();
  const { getGroundById, getServiceById, calculateSlotPrice, getSlotAvailability } = useSlotAvailability();
  const { slotConfiguration } = useSlotConfiguration();
  
  const ground = groundId ? getGroundById(groundId) : null;
  const service = (groundId && serviceId) ? getServiceById(groundId, serviceId) : null;
  
  // Get individual sports services from configuration
  const individualSportsServices = slotConfiguration?.services?.filter(
    config => config.service?.category === 'individual_sports' || config.service?.type === 'individual_sports'
  ) || [];

  // Get pre-selected data from navigation state
  const preselectedData = location.state || null;
  const configuredService = preselectedData?.serviceConfig?.service || null;
  
  // Determine if this is individual sports
  const isIndividualSports = configuredService?.category === 'individual_sports' ||
                             (service?.type !== 'cricket_match' && service?.type !== 'practice_net' && service?.type !== 'open_net');

  const [selectedService, setSelectedService] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('hourly');
  const [selectedDate, setSelectedDate] = useState(
    preselectedData?.preselectedDate 
      ? new Date(preselectedData.preselectedDate) 
      : addDays(new Date(), 1)
  );
  const [selectedSlot, setSelectedSlot] = useState(preselectedData?.preselectedSlot || null);
  const [customPrice, setCustomPrice] = useState(
    preselectedData?.preselectedPrice || null
  );
  const [showPriceEdit, setShowPriceEdit] = useState(false);
  // Slot-flow extras (calendar bookings): book the full slot or share it (half
  // price), and extend across consecutive hours (price = hours × per-hour).
  const [bookingExtent, setBookingExtent] = useState('full'); // 'full' | 'half'
  const [durationHours, setDurationHours] = useState(1);
  
  // Only the full cricket ground match uses the team (two-sides) form. Every
  // other service — open/practice nets, box cricket, all hourly/individual
  // bookings — stays on this simple form. Redirect via effect (not an inline
  // return) so hook order stays stable when `service` loads asynchronously.
  useEffect(() => {
    if (service && service.type === 'cricket_match') {
      navigate(`/dashboard/booking/${groundId}/${serviceId}`, { state: location.state });
    }
  }, [service, groundId, serviceId]); // redirect only the cricket ground match

  // Initialize service if coming from IndividualSlotBooking
  useEffect(() => {
    if (!service && individualSportsServices.length > 0 && !preselectedData) {
      // If no service selected and we have individual sports services, allow user to select
    }
  }, [service, individualSportsServices, preselectedData]);

  // Set pre-selected slot when component mounts with navigation state
  useEffect(() => {
    if (preselectedData?.preselectedSlot && service) {
      // Find matching slot in service's dailySlots
      const matchingSlot = service.dailySlots?.find(
        slot => slot.id === preselectedData.preselectedSlot.id ||
                (slot.startTime === preselectedData.preselectedSlot.startTime &&
                 slot.endTime === preselectedData.preselectedSlot.endTime)
      );
      if (matchingSlot) {
        setSelectedSlot(matchingSlot);
        if (preselectedData.preselectedPrice) {
          setCustomPrice(preselectedData.preselectedPrice);
        }
      }
    }
  }, [preselectedData, service]);

  const [bookingData, setBookingData] = useState({
    name: '',
    contactPhone: '',
    contactEmail: '',
    specialRequests: ''
  });

  const [paymentDetails, setPaymentDetails] = useState({
    paymentMode: 'cash',
    discountCoupon: '',
    advanceAmount: 0
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [addingToWaitlist, setAddingToWaitlist] = useState(false);

  const handleAddToWaitlist = async () => {
    if (!selectedSlot || !selectedDate) return;
    setAddingToWaitlist(true);
    try {
      const dateStr = selectedDate instanceof Date
        ? format(selectedDate, 'yyyy-MM-dd')
        : format(new Date(selectedDate), 'yyyy-MM-dd');
      const res = await waitlistAPI.create({
        ground_id: parseInt(groundId, 10),
        service_id: parseInt(serviceId, 10),
        slot_id: selectedSlot.id || null,
        date: dateStr,
        time_slot: selectedSlot.startTime && selectedSlot.endTime
          ? `${selectedSlot.startTime}-${selectedSlot.endTime}` : selectedSlot.name || null,
        slot_name: selectedSlot.name || null,
        service_type: service?.type || null,
        service_category: service?.category || null,
        contact_name: bookingData.name || 'Guest',
        contact_phone: bookingData.contactPhone || '0000000000',
        contact_email: bookingData.contactEmail || null,
        player_count: 0,
        notes: bookingData.specialRequests || null,
      });
      if (res.success) {
        setErrors({});
        navigate('/dashboard/waitlist');
      } else {
        setErrors(prev => ({ ...prev, slot: res.message || 'Failed to add to waitlist' }));
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, slot: err.message || 'Failed to add to waitlist' }));
    } finally {
      setAddingToWaitlist(false);
    }
  };

  // Calculate price based on selected service and duration
  const calculatePrice = () => {
    if (!selectedService) return 0;
    
    // Get prices from service (new structure with prices object)
    const prices = selectedService.service?.prices || selectedService.priceSetup?.prices || {};
    
    // If prices object exists and has the selected duration, use it
    if (prices && typeof prices === 'object' && Object.keys(prices).length > 0) {
      const priceValue = prices[selectedDuration];
      if (priceValue !== undefined && priceValue !== null && priceValue !== '') {
        const numPrice = typeof priceValue === 'number' ? priceValue : parseFloat(priceValue);
        if (!isNaN(numPrice) && numPrice > 0) {
          return numPrice;
        }
      }
    }
    
    // Fallback to old structure for backward compatibility
    const basePrice = selectedService.service?.price || selectedService.priceSetup?.basePrice || 0;
    if (basePrice > 0) {
      // Calculate based on duration (old calculation method)
      switch (selectedDuration) {
        case 'hourly':
          return basePrice;
        case 'perday':
          return basePrice * 8; // Assuming 8 hours per day
        case 'weekly':
          return basePrice * 8 * 7; // 7 days
        case 'monthly':
          return basePrice * 8 * 30; // 30 days
        default:
          return basePrice;
      }
    }
    
    return 0;
  };
  
  // Get available durations for selected service
  const getAvailableDurations = () => {
    if (!selectedService) return ['monthly', 'weekly', 'perday', 'hourly'];
    
    const prices = selectedService.service?.prices || selectedService.priceSetup?.prices || {};
    
    // Check if prices is a valid object
    if (prices && typeof prices === 'object' && Object.keys(prices).length > 0) {
      const availableDurations = Object.keys(prices).filter(duration => {
        const price = prices[duration];
        const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
        return numPrice > 0;
      });
      
      if (availableDurations.length > 0) {
        return availableDurations;
      }
    }
    
    // If no prices object, return all durations (backward compatibility)
    return ['monthly', 'weekly', 'perday', 'hourly'];
  };

  // Calculate due amount - this will recalculate on every render when dependencies change
  const dueAmount = calculatePrice();
  
  // Allow access if we have individual sports services OR if service exists
  const canAccess = individualSportsServices.length > 0 || service;

  const validateForm = () => {
    const newErrors = {};

    // Date validation
    if (!selectedDate) {
      newErrors.date = 'Date is required. Please select a date.';
    }

    // Slot validation (only required if service has slots)
    if (service && service.dailySlots && service.dailySlots.length > 0 && !selectedSlot) {
      newErrors.slot = 'Time slot is required. Please select a time slot.';
    }

    // Service validation (if no service from URL)
    if (!service && !selectedService) {
      newErrors.service = 'Please select a service';
    }

    // Duration validation
    if (!selectedDuration) {
      newErrors.duration = 'Please select a duration';
    }

    // Contact information validation
    if (!bookingData.name || !bookingData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!bookingData.contactPhone || !bookingData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact number is required';
    } else {
      const phoneDigits = bookingData.contactPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors.contactPhone = 'Please enter a valid 10-digit phone number';
      }
    }

    setErrors(newErrors);
    // GA-005: return the error map so the submit handler can surface a summary
    // and scroll to the first invalid field (setErrors is async).
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      focusFirstInvalidField(validationErrors);
      return;
    }

    // Check slot availability before booking (only if we have groundId and serviceId)
    if (selectedSlot && selectedDate && groundId && serviceId) {
      const dateKey = selectedDate instanceof Date 
        ? format(selectedDate, 'yyyy-MM-dd')
        : format(new Date(selectedDate), 'yyyy-MM-dd');
      const availability = getSlotAvailability(dateKey, parseInt(groundId), parseInt(serviceId));
      // getSlotAvailability returns an object with slotId as keys, not an array
      const slotStatus = availability[selectedSlot.id];
      
      if (slotStatus && slotStatus.status === 'booked') {
        setErrors(prev => ({ 
          ...prev, 
          slot: 'SLOT_FULL'
        }));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Ensure selectedDate is a Date object
      let bookingDate;
      if (selectedDate instanceof Date) {
        bookingDate = selectedDate;
      } else if (typeof selectedDate === 'string') {
        bookingDate = new Date(selectedDate);
      } else {
        bookingDate = new Date(selectedDate);
      }
      
      if (isNaN(bookingDate.getTime())) {
        throw new Error('Invalid date selected');
      }
      
      const dateKey = format(bookingDate, 'yyyy-MM-dd');
      
      // Calculate total amount
      let totalAmount = 0;
      let baseAmount = 0;
      if (service && selectedSlot) {
        // Slot flow: hours × per-hour, halved when shared. Discount is applied
        // by the shared block below, so pass the pre-discount gross as base.
        baseAmount = grossSlot;
        totalAmount = grossSlot;
      } else if (selectedService) {
        totalAmount = calculatePrice();
        baseAmount = totalAmount; // Use total as base if no separate calculation
      }
      
      // Calculate discount
      const discountInputValue = parseFloat(paymentDetails.discountAmount) || 0;
      const discountType = paymentDetails.discountType || 'amount';
      let discountAmount = 0;
      
      if (discountInputValue > 0) {
        if (discountType === 'percentage') {
          discountAmount = (baseAmount * discountInputValue) / 100;
        } else {
          discountAmount = discountInputValue;
        }
      }
      
      // Recalculate total after discount
      totalAmount = Math.max(0, baseAmount - discountAmount);
      
      // Ensure advanceAmount is always a number
      const advanceAmount = parseFloat(paymentDetails.advanceAmount) || 0;
      // Calculate balance amount (ensure it's never negative)
      const balanceAmount = Math.max(0, totalAmount - advanceAmount);

      const activeService = service || selectedService?.service;
      // Get slot name from selected slot
      const slotName = selectedSlot?.name || null;
      
      const newBooking = {
        groundId: groundId ? parseInt(groundId) : null,
        serviceId: serviceId ? parseInt(serviceId) : null,
        slotId: selectedSlot?.id || null,
        date: dateKey,
        timeSlot: selectedSlot
          ? `${selectedSlot.startTime}-${isSlotFlow ? computedEndTime : selectedSlot.endTime}`
          : null,
        slotName: slotName,
        serviceType: activeService?.type || 'individual_sports',
        serviceCategory: 'individual_sports',
        serviceName: activeService?.name || selectedService?.service?.name,
        groundName: ground?.name || null,
        // Half-share bookings occupy half the slot; full bookings take it all.
        bookingType: isSlotFlow ? bookingExtent : undefined,
        duration: isSlotFlow ? `${durationHours}h` : selectedDuration,
        ...bookingData,
        // Map name to contactName for Notification component compatibility
        contactName: bookingData.name || bookingData.contactName,
        contactPhone: bookingData.contactPhone,
        contactEmail: bookingData.contactEmail,
        customPrice,
        paymentDetails: {
          ...paymentDetails,
          // Ensure payment details are properly structured
          advanceAmount: advanceAmount,
          discountAmount: discountAmount,
          discountType: discountType,
          baseAmount: baseAmount,
          totalAmount: totalAmount
        },
        baseAmount,
        discountAmount,
        totalAmount,
        advanceAmount,
        balanceAmount,
        bookingDate: new Date().toISOString()
      };

      const confirmed = await addBooking(newBooking);
      // Ensure we preserve all values even if API doesn't return them
      const finalBooking = {
        ...confirmed,
        // Preserve date and slot details
        date: confirmed?.date || newBooking.date,
        timeSlot: confirmed?.timeSlot || newBooking.timeSlot,
        slotName: confirmed?.slotName || newBooking.slotName,
        slotId: confirmed?.slotId || newBooking.slotId,
        // Preserve service and ground details
        serviceName: confirmed?.serviceName || newBooking.serviceName,
        groundName: confirmed?.groundName || newBooking.groundName,
        serviceType: confirmed?.serviceType || newBooking.serviceType,
        // Preserve our calculated values
        totalAmount: confirmed?.totalAmount || newBooking.totalAmount,
        advanceAmount: confirmed?.advanceAmount !== undefined ? confirmed.advanceAmount : newBooking.advanceAmount,
        balanceAmount: confirmed?.balanceAmount !== undefined ? confirmed.balanceAmount : newBooking.balanceAmount,
        baseAmount: confirmed?.baseAmount || newBooking.baseAmount,
        discountAmount: confirmed?.discountAmount !== undefined ? confirmed.discountAmount : newBooking.discountAmount,
        // Preserve contact information
        contactName: confirmed?.contactName || newBooking.contactName,
        contactPhone: confirmed?.contactPhone || newBooking.contactPhone,
        contactEmail: confirmed?.contactEmail || newBooking.contactEmail,
        // Preserve payment details
        paymentDetails: {
          ...(confirmed?.paymentDetails || {}),
          ...(newBooking.paymentDetails || {}),
          baseAmount: confirmed?.baseAmount || newBooking.baseAmount,
          discountAmount: confirmed?.discountAmount !== undefined ? confirmed.discountAmount : newBooking.discountAmount,
          totalAmount: confirmed?.totalAmount || newBooking.totalAmount,
          advanceAmount: confirmed?.advanceAmount !== undefined ? confirmed.advanceAmount : newBooking.advanceAmount
        }
      };
      setIsSubmitting(false);
      setShowSuccess(true);
      setConfirmedBooking(finalBooking);
    } catch (error) {
      console.error('=== BOOKING ERROR ===', error);
      setIsSubmitting(false);
      setErrors(prev => ({ ...prev, submit: `Failed to submit booking: ${error.message || 'Please try again.'}` }));
    }
  };

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const currentPrice = customPrice ||
    (selectedService ? calculatePrice() :
     (selectedSlot && service ? calculateSlotPrice(groundId, serviceId, selectedSlot.id) : 0));

  // ── Slot-flow pricing (calendar bookings) ──────────────────────────────────
  // Per-hour rate for the selected slot, the max hours bookable from it, the
  // resulting end time and the extent-adjusted total.
  const perHourPrice = Number(
    preselectedData?.preselectedPrice ??
    selectedSlot?.defaultPrice ??
    selectedSlot?.price ??
    customPrice ?? 0
  ) || 0;

  const maxHours = (() => {
    if (!service?.dailySlots?.length || !selectedSlot) return 8;
    const idx = service.dailySlots.findIndex(
      (s) => s.id === selectedSlot.id ||
        (s.startTime === selectedSlot.startTime && s.endTime === selectedSlot.endTime)
    );
    if (idx < 0) return 8;
    return Math.max(1, service.dailySlots.length - idx);
  })();

  const extentFactor = bookingExtent === 'half' ? 0.5 : 1;
  const grossSlot = perHourPrice * durationHours * extentFactor;
  const slotDiscount = (() => {
    const v = parseFloat(paymentDetails.discountAmount) || 0;
    if (v <= 0) return 0;
    return paymentDetails.discountType === 'percentage'
      ? Math.min(grossSlot, (grossSlot * v) / 100)
      : Math.min(grossSlot, v);
  })();
  const slotTotal = Math.max(0, grossSlot - slotDiscount);

  const computedEndTime = (() => {
    if (!selectedSlot?.startTime) return selectedSlot?.endTime || '';
    const [h, m] = selectedSlot.startTime.split(':').map(Number);
    const end = (h || 0) + durationHours;
    return `${String(end).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
  })();

  // The slot-flow shows its own price/payment block; this is its due amount.
  const slotAdvance = parseFloat(paymentDetails.advanceAmount) || 0;
  const slotBalance = Math.max(0, slotTotal - slotAdvance);
  const isSlotFlow = Boolean(service && selectedSlot && !selectedService);

  // GA-005: invalid fields for the pre-submit summary (SLOT_FULL has its own
  // dedicated waitlist UI, so it is excluded here).
  const errorFieldKeys = Object.keys(errors).filter(
    (k) => errors[k] && k !== 'submit' && errors[k] !== 'SLOT_FULL'
  );

  // Show error if no services available
  if (!canAccess) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="card text-center py-12">
            <AlertCircle className="h-16 w-16 text-ga-danger mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ga-text-primary mb-2">No Services Available</h2>
            <p className="text-ga-text-muted mb-4">
              Please configure individual sports services first.
            </p>
            <button onClick={() => navigate('/dashboard/config-individual-service')} className="btn-primary">
              Go to Configuration
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-1 sm:px-0">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-ga-text-primary mb-2">Individual Slot Booking</h1>
          <p className="text-ga-text-muted">Book individual sports slots with flexible duration options</p>
        </div>
        
        <div className="card">
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information Section - First */}
          <div className="space-y-6 border-b pb-6">
            <h3 className="text-lg font-semibold text-ga-text-primary flex items-center gap-2">
              Contact Information
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  data-field="name"
                  value={bookingData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="text-ga-danger text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">
                  Contact Number *
                </label>
                <input
                  type="tel"
                  data-field="contactPhone"
                  value={bookingData.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter 10-digit phone number"
                />
                {errors.contactPhone && (
                  <p className="text-ga-danger text-sm mt-1">{errors.contactPhone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Service Selection Section */}
          {!service && individualSportsServices.length > 0 && (
            <div className="space-y-6 border-b pb-6">
              <h3 className="text-lg font-semibold text-ga-text-primary">Service Selection</h3>
              
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Select a Service (dropdown) *
                </label>
                <select
                  data-field="service"
                  value={selectedService ? selectedService.service?.name : ''}
                  onChange={(e) => {
                    const serviceConfig = individualSportsServices.find(
                      s => s.service?.name === e.target.value
                    );
                    setSelectedService(serviceConfig);
                    setErrors(prev => ({ ...prev, service: '' }));
                    
                    // Reset duration to first available duration for the selected service
                    if (serviceConfig) {
                      const prices = serviceConfig.service?.prices || serviceConfig.priceSetup?.prices || {};
                      const availableDurations = Object.keys(prices).filter(duration => {
                        const price = prices[duration];
                        const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
                        return numPrice > 0;
                      });
                      if (availableDurations.length > 0) {
                        setSelectedDuration(availableDurations[0]);
                      } else {
                        // If no prices configured, default to hourly
                        setSelectedDuration('hourly');
                      }
                    }
                  }}
                  className="input-field w-full"
                >
                  <option value="">Select a service...</option>
                  {individualSportsServices.map((config, index) => (
                    <option key={index} value={config.service?.name}>
                      {config.service?.name}
                    </option>
                  ))}
                </select>
                {errors.service && (
                  <p className="text-ga-danger text-sm mt-1">{errors.service}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Duration (dropdown) *
                </label>
                <select
                  data-field="duration"
                  value={selectedDuration}
                  onChange={(e) => {
                    setSelectedDuration(e.target.value);
                    setErrors(prev => ({ ...prev, duration: '' }));
                  }}
                  className="input-field w-full"
                >
                  {getAvailableDurations().map(duration => {
                    const labels = {
                      monthly: 'Monthly',
                      weekly: 'Weekly',
                      perday: 'Per Day',
                      hourly: 'Hourly'
                    };
                    return (
                      <option key={duration} value={duration}>
                        {labels[duration] || duration}
                      </option>
                    );
                  })}
                </select>
                {errors.duration && (
                  <p className="text-ga-danger text-sm mt-1">{errors.duration}</p>
                )}
                {selectedService && getAvailableDurations().length === 0 && (
                  <p className="text-ga-warning text-sm mt-1">
                    No prices configured for this service. Please configure prices first.
                  </p>
                )}
              </div>

              {selectedService && (
                <div className="bg-ga-bg-surface-subtle rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <span className="text-ga-text-muted font-medium">Price ({selectedDuration}):</span>
                    <span className="text-lg font-bold text-ga-primary">
                      ₹{calculatePrice().toFixed(2)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mt-2">
                    <span className="text-ga-text-muted font-semibold">Due Amount:</span>
                    <span className="text-xl font-bold text-ga-primary">
                      ₹{dueAmount.toFixed(2)}
                    </span>
                  </div>
                  {/* Show all available prices */}
                  {(() => {
                    const prices = selectedService.service?.prices || selectedService.priceSetup?.prices || {};
                    const hasPrices = prices && typeof prices === 'object' && Object.keys(prices).length > 0;
                    if (hasPrices) {
                      return (
                        <div className="mt-3 pt-3 border-t border-ga-border-subtle">
                          <p className="text-xs text-ga-text-muted mb-2">Available Pricing:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            {Object.entries(prices).map(([duration, price]) => {
                              const labels = {
                                monthly: 'Monthly',
                                weekly: 'Weekly',
                                perday: 'Per Day',
                                hourly: 'Hourly'
                              };
                              const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
                              if (numPrice > 0) {
                                return (
                                  <div key={duration} className={`flex justify-between p-1 rounded ${duration === selectedDuration ? 'bg-ga-success/10 border border-ga-success/30' : ''}`}>
                                    <span className="text-ga-text-muted">{labels[duration]}:</span>
                                    <span className="font-semibold text-ga-text-primary">₹{numPrice}</span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Date & Time Selection */}
          <div className="space-y-6 border-b pb-6">
            <h3 className="text-lg font-semibold text-ga-text-primary">Date and Time Selection</h3>
            
            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-ga-text-muted mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                data-field="date"
                value={selectedDate 
                  ? (selectedDate instanceof Date 
                      ? format(selectedDate, 'yyyy-MM-dd')
                      : format(new Date(selectedDate), 'yyyy-MM-dd'))
                  : ''}
                onChange={(e) => {
                  setSelectedDate(new Date(e.target.value));
                  setErrors(prev => ({ ...prev, date: '' }));
                }}
                min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                className="input-field w-full"
                disabled={!!preselectedData?.preselectedDate}
              />
              {preselectedData?.preselectedDate && (
                <p className="text-xs text-ga-text-subtle mt-1">Date selected from calendar</p>
              )}
              {errors.date && (
                <p className="text-ga-danger text-sm mt-1">{errors.date}</p>
              )}
            </div>

            {/* Time Selection - for individual sports */}
            {!service || !service.dailySlots || service.dailySlots.length === 0 ? (
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Time *
                </label>
                <input
                  type="time"
                  data-field="slot"
                  value={selectedSlot ? selectedSlot.startTime : ''}
                  onChange={(e) => {
                    const timeValue = e.target.value;
                    if (timeValue) {
                      const [hours, minutes] = timeValue.split(':');
                      const endTime = `${parseInt(hours) + 1}:${minutes}`;
                      setSelectedSlot({
                        id: null, // custom time: no configured_slots.id; backend resolves by time_slot
                        startTime: timeValue,
                        endTime: endTime
                      });
                      setErrors(prev => ({ ...prev, slot: '' }));
                    }
                  }}
                  className="input-field w-full"
                />
                {errors.slot && (
                  errors.slot === 'SLOT_FULL' ? (
                    <div className="mt-2 p-3 rounded-lg border border-ga-warning/30 bg-ga-warning/10">
                      <p className="text-sm text-ga-warning font-medium">This slot is fully booked.</p>
                      <p className="text-xs text-ga-warning mt-1">You can add to the waitlist and get notified when a slot opens.</p>
                      <button
                        onClick={handleAddToWaitlist}
                        disabled={addingToWaitlist}
                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ga-text-on-primary bg-ga-warning hover:brightness-110 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <ListOrdered className="h-3.5 w-3.5" />
                        {addingToWaitlist ? 'Adding…' : 'Add to Waitlist'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-ga-danger text-sm mt-1">{errors.slot}</p>
                  )
                )}
              </div>
            ) : (
              /* Time Slot Selection (only if service has slots) */
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Select Time Slot *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-field="slot">
                  {service.dailySlots.map((slot) => {
                  const isPreselected = preselectedData?.preselectedSlot && 
                                       (slot.id === preselectedData.preselectedSlot.id ||
                                        (slot.startTime === preselectedData.preselectedSlot.startTime &&
                                         slot.endTime === preselectedData.preselectedSlot.endTime));
                  const isSelected = selectedSlot && 
                                     (slot.id === selectedSlot.id ||
                                      (slot.startTime === selectedSlot.startTime &&
                                       slot.endTime === selectedSlot.endTime));
                  
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-3 text-sm font-medium rounded-lg border transition-colors duration-200 relative ${
                        isPreselected
                          ? 'border-ga-info bg-ga-info/10 text-ga-info'
                          : isSelected
                          ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                          : 'border-ga-border-subtle hover:border-ga-primary hover:bg-ga-bg-surface-subtle'
                      }`}
                    >
                      {isPreselected && (
                        <div className="absolute top-1 right-1">
                          <Calendar className="h-4 w-4 text-ga-primary" />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span>{slot.startTime} - {slot.endTime}</span>
                        {isPreselected && (
                          <span className="text-xs text-ga-primary ml-2">From Calendar</span>
                        )}
                      </div>
                      <div className="text-xs text-ga-text-muted mt-1">₹{slot.defaultPrice || 0}</div>
                    </button>
                  );
                  })}
                </div>
                {errors.slot && (
                  errors.slot === 'SLOT_FULL' ? (
                    <div className="mt-2 p-3 rounded-lg border border-ga-warning/30 bg-ga-warning/10">
                      <p className="text-sm text-ga-warning font-medium">This slot is fully booked.</p>
                      <p className="text-xs text-ga-warning mt-1">You can add to the waitlist and get notified when a slot opens.</p>
                      <button
                        onClick={handleAddToWaitlist}
                        disabled={addingToWaitlist}
                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ga-text-on-primary bg-ga-warning hover:brightness-110 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <ListOrdered className="h-3.5 w-3.5" />
                        {addingToWaitlist ? 'Adding…' : 'Add to Waitlist'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-ga-danger text-sm mt-1">{errors.slot}</p>
                  )
                )}
              </div>
            )}

            {/* Selected Date & Slot Info Banner (if preselected) */}
            {preselectedData?.preselectedDate && preselectedData?.preselectedSlot && (
              <div className="bg-ga-success/10 border border-ga-success/30 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-ga-primary" />
                    <div>
                      <p className="text-sm text-ga-text-muted">Selected Date</p>
                      <p className="font-semibold text-ga-text-primary">
                        {format(selectedDate instanceof Date ? selectedDate : new Date(selectedDate), 'MMMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-ga-primary" />
                    <div>
                      <p className="text-sm text-ga-text-muted">Time Slot</p>
                      <p className="font-semibold text-ga-text-primary">
                        {selectedSlot?.startTime} - {selectedSlot?.endTime}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Slot-flow: booking extent, duration extension, price & advance */}
          {isSlotFlow && (
            <div className="space-y-5 border-b pb-6">
              <h3 className="text-lg font-semibold text-ga-text-primary">Booking details</h3>

              {/* Full vs Half (shared) */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">Booking type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setBookingExtent('full')}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      bookingExtent === 'full'
                        ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                        : 'border-ga-border-subtle text-ga-text-muted hover:border-ga-primary'
                    }`}
                  >
                    <div className="font-semibold">Full slot</div>
                    <div className="text-xs mt-1">One customer takes the whole slot</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingExtent('half')}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      bookingExtent === 'half'
                        ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                        : 'border-ga-border-subtle text-ga-text-muted hover:border-ga-primary'
                    }`}
                  >
                    <div className="font-semibold">Half (shared)</div>
                    <div className="text-xs mt-1">Share the slot · pays 50%</div>
                  </button>
                </div>
              </div>

              {/* Duration extension */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Duration · {durationHours} hr{durationHours > 1 ? 's' : ''}
                  <span className="ml-2 font-normal text-ga-text-subtle">
                    ({selectedSlot?.startTime} – {computedEndTime})
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4].filter((h) => h <= maxHours).map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setDurationHours(h)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        durationHours === h
                          ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                          : 'border-ga-border-subtle text-ga-text-muted hover:border-ga-primary'
                      }`}
                    >
                      {h} hr{h > 1 ? 's' : ''}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setDurationHours(maxHours)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      durationHours === maxHours && maxHours > 4
                        ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                        : 'border-ga-border-subtle text-ga-text-muted hover:border-ga-primary'
                    }`}
                  >
                    Full day ({maxHours} hrs)
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-ga-text-subtle">
                  ₹{perHourPrice.toLocaleString('en-IN')}/hr × {durationHours} hr{durationHours > 1 ? 's' : ''}
                  {bookingExtent === 'half' ? ' × ½ (shared)' : ''}
                </p>
              </div>

              {/* Discount */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" /> Discount (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentDetails.discountAmount || ''}
                    onChange={(e) => setPaymentDetails((prev) => ({ ...prev, discountAmount: e.target.value }))}
                    className="input-field flex-1"
                    placeholder="0"
                  />
                  <div className="flex rounded-lg border border-ga-border-subtle overflow-hidden">
                    {[{ id: 'amount', label: '₹' }, { id: 'percentage', label: '%' }].map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setPaymentDetails((prev) => ({ ...prev, discountType: t.id }))}
                        className={`px-4 text-sm font-semibold transition-colors ${
                          (paymentDetails.discountType || 'amount') === t.id
                            ? 'bg-ga-primary text-ga-text-on-primary'
                            : 'bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Price summary */}
              <div className="bg-ga-bg-surface-subtle rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ga-text-muted">
                    {bookingExtent === 'half' ? 'Half slot' : 'Full slot'} · {durationHours} hr{durationHours > 1 ? 's' : ''}
                  </span>
                  <span className="font-medium text-ga-text-primary">₹{grossSlot.toLocaleString('en-IN')}</span>
                </div>
                {slotDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ga-text-muted">Discount</span>
                    <span className="font-medium text-ga-success">− ₹{slotDiscount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="font-semibold text-ga-text-primary">Total</span>
                  <span className="text-xl font-bold text-ga-primary">₹{slotTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Payment mode */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">Payment mode</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ id: 'cash', label: 'Cash' }, { id: 'online', label: 'Online' }].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPaymentDetails((prev) => ({ ...prev, paymentMode: m.id }))}
                      className={`p-3 rounded-lg border-2 font-semibold transition-colors ${
                        paymentDetails.paymentMode === m.id
                          ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                          : 'border-ga-border-subtle text-ga-text-muted hover:border-ga-primary'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Advance to reserve, balance on arrival */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Advance amount (₹) — pay rest on arrival
                </label>
                <input
                  type="number"
                  min="0"
                  max={slotTotal}
                  step="0.01"
                  value={paymentDetails.advanceAmount || ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                    const clamped = Math.min(Math.max(0, v), slotTotal);
                    setPaymentDetails((prev) => ({ ...prev, advanceAmount: clamped }));
                  }}
                  className="input-field w-full"
                  placeholder="0 = pay full on arrival"
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-ga-text-subtle">Advance: ₹{slotAdvance.toLocaleString('en-IN')}</span>
                  <span className="font-medium text-ga-warning">Balance on arrival: ₹{slotBalance.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Email and Special Requests (Optional) */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ga-text-muted mb-1">
                Email Address (Optional)
              </label>
              <input
                type="email"
                value={bookingData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                className="input-field w-full"
                placeholder="Enter your email address"
              />
              {errors.contactEmail && (
                <p className="text-ga-danger text-sm mt-1">{errors.contactEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-ga-text-muted mb-1">
                Special Requests (Optional)
              </label>
              <textarea
                value={bookingData.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                className="input-field w-full"
                rows="3"
                placeholder="Any special requests or notes..."
              />
            </div>
          </div>

          {/* Price and Due Amount Calculation */}
          {selectedService && (
            <div className="border-t pt-6 space-y-4">
              <h3 className="text-lg font-semibold text-ga-text-primary flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-ga-primary" />
                Price & Due Amount
              </h3>
              
              <div className="bg-ga-bg-surface-subtle rounded-lg p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-ga-text-muted font-medium">Service Price ({selectedDuration}):</span>
                  <span className="text-lg font-bold text-ga-primary">
                    ₹{calculatePrice().toFixed(2)}
                  </span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-ga-text-muted font-medium">Duration:</span>
                  <span className="text-ga-text-primary font-medium capitalize">
                    {selectedDuration}
                  </span>
                </div>
                
                <div className="border-t pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-ga-text-muted font-semibold text-lg">Due Amount:</span>
                  <span className="text-2xl font-bold text-ga-primary">
                    ₹{dueAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Payment Mode *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentDetails(prev => ({ ...prev, paymentMode: 'cash' }))}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      paymentDetails.paymentMode === 'cash'
                        ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                        : 'border-ga-border-subtle ga-card text-ga-text-muted hover:border-ga-primary'
                    }`}
                  >
                    <div className="font-semibold">Cash</div>
                    <div className="text-xs mt-1">Pay in cash</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentDetails(prev => ({ ...prev, paymentMode: 'online' }))}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      paymentDetails.paymentMode === 'online'
                        ? 'border-ga-primary bg-ga-primary-soft text-ga-primary'
                        : 'border-ga-border-subtle ga-card text-ga-text-muted hover:border-ga-primary'
                    }`}
                  >
                    <div className="font-semibold">Online</div>
                    <div className="text-xs mt-1">UPI / Card / Net Banking</div>
                  </button>
                </div>
              </div>

              {/* Discount Coupon */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Discount Coupon (Optional)
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={paymentDetails.discountCoupon}
                    onChange={(e) => setPaymentDetails(prev => ({ ...prev, discountCoupon: e.target.value }))}
                    className="input-field flex-1"
                    placeholder="Enter coupon code"
                  />
                  <button
                    type="button"
                    className="px-4 py-2 bg-ga-bg-surface-subtle hover:bg-ga-bg-surface text-ga-text-muted rounded-lg transition-colors text-sm font-medium w-full sm:w-auto"
                    onClick={() => {
                      alert('Coupon validation will be implemented');
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Advance Amount */}
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  Advance Amount (₹) *
                </label>
                <input
                  type="number"
                  min="0"
                  max={dueAmount}
                  step="0.01"
                  value={paymentDetails.advanceAmount || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const value = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
                    const maxValue = dueAmount;
                    const clampedValue = value > maxValue ? maxValue : (value < 0 ? 0 : value);
                    setPaymentDetails(prev => ({ 
                      ...prev, 
                      advanceAmount: clampedValue
                    }));
                  }}
                  className="input-field w-full"
                  placeholder="Enter advance amount"
                  required
                />
                {paymentDetails.advanceAmount > dueAmount && (
                  <p className="text-ga-danger text-sm mt-1">Advance amount cannot exceed due amount</p>
                )}
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs gap-1">
                  <span className="text-ga-text-subtle">Minimum: ₹0</span>
                  <span className="text-ga-text-subtle">Maximum: ₹{dueAmount.toFixed(2)}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentDetails(prev => ({ 
                      ...prev, 
                      advanceAmount: Math.round(dueAmount * 0.5) 
                    }))}
                    className="px-3 py-1 text-xs bg-ga-bg-surface-subtle hover:bg-ga-bg-surface text-ga-text-muted rounded transition-colors"
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentDetails(prev => ({ 
                      ...prev, 
                      advanceAmount: Math.round(dueAmount * 0.75) 
                    }))}
                    className="px-3 py-1 text-xs bg-ga-bg-surface-subtle hover:bg-ga-bg-surface text-ga-text-muted rounded transition-colors"
                  >
                    75%
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentDetails(prev => ({ 
                      ...prev, 
                      advanceAmount: dueAmount 
                    }))}
                    className="px-3 py-1 text-xs bg-ga-bg-surface-subtle hover:bg-ga-bg-surface text-ga-text-muted rounded transition-colors"
                  >
                    Full Payment
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-ga-danger/10 border border-ga-danger/30 rounded-lg p-3">
              <p className="text-ga-danger text-sm">{errors.submit}</p>
            </div>
          )}

          {/* GA-005: compact error summary right above the submit button so a
              failed submit is never silent. */}
          {errorFieldKeys.length > 0 && (
            <InfoBanner
              variant="danger"
              title="Please fix the following before submitting"
            >
              <ul className="list-disc space-y-0.5 pl-4">
                {errorFieldKeys.map((k) => (
                  <li key={k}>
                    {FIELD_LABELS[k] || k}: {errors[k]}
                  </li>
                ))}
              </ul>
            </InfoBanner>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Confirming Booking...' : 'Submit Booking'}
          </button>
        </form>
      </div>
      
      {/* Notification Modal */}
      {showSuccess && confirmedBooking && (
        <Notification
          booking={confirmedBooking}
          onClose={() => {
            setShowSuccess(false);
            setConfirmedBooking(null);
            navigate('/dashboard/my-bookings');
          }}
          onConfirm={() => {
            setShowSuccess(false);
            setConfirmedBooking(null);
            navigate('/dashboard/my-bookings');
          }}
        />
      )}
      </div>
    </DashboardLayout>
  );
};

export default BookingNew;
